import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import UsersList from './pages/users/UsersList';
import UserDetail from './pages/users/UserDetail';
import RolesList from './pages/roles/RolesList';
import CandidatesList from './pages/candidates/CandidatesList';
import CandidateRegister from './pages/candidates/CandidateRegister';
import CandidateWorkspace from './pages/candidates/CandidateWorkspace';
import PendingStepEdits from './pages/candidates/PendingStepEdits';
import LeadsList from './pages/leads/LeadsList';
import LeadRegister from './pages/leads/LeadRegister';
import LeadDetail from './pages/leads/LeadDetail';
import LeadMasters from './pages/masters/LeadMasters';
import NationalityMasters from './pages/masters/NationalityMasters';
import WorkflowReference from './pages/workflow/WorkflowReference';
import WorkflowDocuments from './pages/workflow/WorkflowDocuments';
import Reports from './pages/reports/Reports';
import ActivityLog from './pages/activity/ActivityLog';

const LEADS_VIEW_PERM = ['leads.view-own', 'leads.view-all'];
const MASTERS_PERM = ['masters.view', 'masters.manage'];

// Central route table: every authenticated route names the permission that guards it
// (null = auth only, no specific permission; an array means any one of those permissions is
// enough). `nav` marks entries that appear in the sidebar — an optional `nav.group` nests the
// entry under a collapsible parent instead of showing it at the top level. App.js renders
// `routes`, Sidebar.jsx renders the `navItems` tree built from the `nav` subset.
export const routes = [
  { path: '/', perm: 'dashboard.view', element: <Dashboard />, nav: { label: 'Dashboard', order: 1 } },
  {
    path: '/candidates',
    perm: 'candidates.view',
    element: <CandidatesList />,
    nav: { label: 'All Candidates', order: 2, group: 'Candidates' },
  },
  {
    path: '/candidates/pending-edits',
    perm: 'approve edit candidate completed step',
    element: <PendingStepEdits />,
    nav: { label: 'Pending Approvals', order: 2.1, group: 'Candidates' },
  },
  { path: '/candidates/new', perm: 'candidates.create', element: <CandidateRegister /> },
  { path: '/candidates/:id', perm: 'candidates.view', element: <CandidateWorkspace /> },
  {
    path: '/leads',
    perm: LEADS_VIEW_PERM,
    element: <LeadsList />,
    nav: { label: 'Leads', order: 3 },
  },
  { path: '/leads/new', perm: 'leads.create', element: <LeadRegister /> },
  { path: '/leads/:id', perm: LEADS_VIEW_PERM, element: <LeadDetail /> },
  {
    path: '/users',
    perm: 'users.view',
    element: <UsersList />,
    nav: { label: 'Users', order: 4 },
  },
  { path: '/users/:id', perm: 'users.view', element: <UserDetail /> },
  {
    path: '/roles',
    perm: 'roles.view',
    element: <RolesList />,
    nav: { label: 'Roles & Permissions', order: 5 },
  },
  {
    path: '/workflow',
    perm: 'workflow.view',
    element: <WorkflowReference />,
    nav: { label: 'Steps', order: 6, group: 'Workflow' },
  },
  {
    path: '/workflow/documents',
    perm: ['workflow.view', 'workflow.documents.manage'],
    element: <WorkflowDocuments />,
    nav: { label: 'Documents', order: 6.1, group: 'Workflow' },
  },
  {
    path: '/reports',
    perm: 'reports.view',
    element: <Reports />,
    nav: { label: 'Reports', order: 7 },
  },
  {
    path: '/activity-log',
    perm: 'activity-logs.view',
    element: <ActivityLog />,
    nav: { label: 'Activity Log', order: 8 },
  },
  {
    path: '/masters/leads',
    perm: MASTERS_PERM,
    element: <LeadMasters />,
    nav: { label: 'Lead Masters', order: 9, group: 'Masters' },
  },
  {
    path: '/masters/nationalities',
    perm: MASTERS_PERM,
    element: <NationalityMasters />,
    nav: { label: 'Nationalities', order: 10, group: 'Masters' },
  },
  { path: '/profile', perm: null, element: <Profile /> },
];

// Builds the sidebar tree: top-level entries, plus one level of grouped entries (e.g. everything
// under "Masters") ordered by the position of the first child that introduces the group.
function buildNavTree() {
  const navRoutes = routes.filter((r) => r.nav).sort((a, b) => a.nav.order - b.nav.order);
  const tree = [];
  const groupNodes = new Map();

  for (const route of navRoutes) {
    const entry = { path: route.path, perm: route.perm, label: route.nav.label };

    if (route.nav.group) {
      let groupNode = groupNodes.get(route.nav.group);
      if (!groupNode) {
        groupNode = { label: route.nav.group, order: route.nav.order, children: [] };
        groupNodes.set(route.nav.group, groupNode);
        tree.push(groupNode);
      }
      groupNode.children.push(entry);
    } else {
      tree.push({ ...entry, order: route.nav.order });
    }
  }

  return tree.sort((a, b) => a.order - b.order);
}

export const navItems = buildNavTree();
